// apps/website/src/App.tsx

import { Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { ToastProvider } from '@dopomogai/ui';

import type { SupabaseClient } from '@dopomogai/supabase-client';

import { AppProviders } from './providers/AppProviders';
import { AppRoutes } from './routes/AppRoutes';
import { LoadingSpinner } from './components/LoadingSpinner';
import { SessionInitializer } from './components/SessionInitializer';
import { PageViewTracker } from './components/PageViewTracker';
import { NavEventBridge } from './components/NavEventBridge';
import { GoogleAnalyticsTracker } from './components/GoogleAnalyticsTracker';
import { SmartlookTracker } from './components/SmartlookTracker';
import { getSiteOrigin } from './config/site';

export function App({ supabaseClient }: { supabaseClient: SupabaseClient }) {
  const siteUrl = getSiteOrigin();
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Dopomogai',
    url: siteUrl,
    logo: `${siteUrl}/favicon.svg`,
    description: 'Dopomogai builds AI-first companies and products.',
    sameAs: ['https://www.linkedin.com/company/dopomogai/']
  };

  return (
    <AppProviders supabaseClient={supabaseClient}>
      <ToastProvider placement="bottom-right" defaultDuration={5000}>
        <Helmet>
          <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
          <meta name="description" content="DopomogAI Platform - Build in Public" />
          <script type="application/ld+json">{JSON.stringify(orgSchema).replace(/</g, '\\u003c')}</script>
        </Helmet>

        {/* Initialize all trackers and session handlers here */}
        <SessionInitializer />
        <PageViewTracker />
        <GoogleAnalyticsTracker />
        <SmartlookTracker />
        <NavEventBridge />

        <Suspense fallback={<LoadingSpinner />}>
          <AppRoutes />
        </Suspense>
      </ToastProvider>
    </AppProviders>
  );
}

// Keep public API stable for pages that import from App.tsx
export { usePageAction } from './routes/outlet-context';

export default App;
